/**
 * Strip Outlook noise from mail bodies before rendering in the feed expand panel.
 * Signatures, quoted threads, tracking pixels, and inline logos are removed so
 * operators see the operational content first.
 */

const REPLY_BREAK_PATTERNS = [
  /^-{2,}\s*original message\s*-{2,}$/i,
  /^-{2,}\s*forwarded message\s*-{2,}$/i,
  /^on .+ wrote:$/i,
  /^from:\s.+/i,
  /^sent:\s.+/i,
  /^-----original message-----$/i,
];

const SIGNATURE_MARKERS = [
  /^--\s*$/,
  /^sent from my /i,
  /^get outlook for /i,
  /^confidentiality notice/i,
  /^this email and any attachments/i,
  /^please consider the environment/i,
  /^disclaimer:/i,
];

/** Remove quoted reply / forward blocks from a plain-text body. */
export function stripQuotedReplies(text: string): string {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const kept: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (/^>/.test(trimmed)) continue;
    if (kept.length >= 1 && REPLY_BREAK_PATTERNS.some((re) => re.test(trimmed))) {
      break;
    }
    kept.push(line);
  }
  return kept.join("\n").trimEnd();
}

/** Drop common signature / legal boilerplate tails. */
export function stripSignature(text: string): string {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i]!.trim();
    if (SIGNATURE_MARKERS.some((re) => re.test(trimmed))) {
      return lines.slice(0, i).join("\n").trimEnd();
    }
  }
  return lines.join("\n").trimEnd();
}

/** Normalize whitespace for readable plain-text display. */
export function normalizePlainText(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Full plain-text cleanup pipeline. */
export function cleanEmailPlainText(raw: string): string {
  let text = stripQuotedReplies(raw);
  text = stripSignature(text);
  return normalizePlainText(text);
}

const NOISE_SELECTOR =
  "script, style, noscript, meta, link, head, svg, iframe, object, embed";

/** Remove inline clutter from an HTML fragment before sandboxed render. */
export function cleanEmailHtml(html: string): string {
  if (typeof DOMParser === "undefined") return html;

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll(NOISE_SELECTOR).forEach((el) => el.remove());

  doc.querySelectorAll("img").forEach((img) => {
    const w = Number.parseInt(img.getAttribute("width") ?? "", 10);
    const h = Number.parseInt(img.getAttribute("height") ?? "", 10);
    const src = img.getAttribute("src") ?? "";
    if (
      (w > 0 && w <= 2) ||
      (h > 0 && h <= 2) ||
      /signature|logo|banner|tracking|pixel|spacer/i.test(src) ||
      /signature|logo|banner|footer|disclaimer/i.test(img.className) ||
      /signature|logo|banner|footer|disclaimer/i.test(img.id)
    ) {
      img.remove();
      return;
    }
    img.removeAttribute("width");
    img.removeAttribute("height");
    img.setAttribute("loading", "lazy");
  });

  doc.querySelectorAll("[style]").forEach((el) => {
    const style = el.getAttribute("style") ?? "";
    if (/display\s*:\s*none/i.test(style) || /visibility\s*:\s*hidden/i.test(style)) {
      el.remove();
    }
  });

  doc.querySelectorAll("table[role='presentation']").forEach((table) => {
    table.removeAttribute("width");
    table.removeAttribute("height");
  });

  return doc.body.innerHTML.trim();
}

/** Minimal reader stylesheet injected into the sandboxed HTML iframe. */
export const EMAIL_BODY_IFRAME_CSS = `
  html, body {
    margin: 0;
    padding: 0;
    background: transparent;
    color: inherit;
    font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
    font-size: 13px;
    line-height: 1.55;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  body { padding: 0.25rem 0; }
  a { color: #2563eb; text-decoration: underline; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; border-collapse: collapse; }
  td, th { vertical-align: top; }
  blockquote {
    margin: 0.5rem 0;
    padding-left: 0.75rem;
    border-left: 2px solid rgba(128,128,128,0.35);
    color: rgba(128,128,128,0.9);
  }
  pre, code { font-family: ui-monospace, monospace; font-size: 12px; }
`;

/** Wrap cleaned HTML with the reader stylesheet for iframe injection. */
export function wrapEmailHtmlDocument(cleanedHtml: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${EMAIL_BODY_IFRAME_CSS}</style></head><body>${cleanedHtml}</body></html>`;
}
